import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MissionPlace } from 'src/app/shared/models/mission/mission-place';
import { LogService } from 'src/app/core/services/log/log.service';
import { FacadeService } from 'src/app/core/services/facade.service';
import { User } from 'src/app/shared/models/security/user';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { MissionPlaceModalAjoutFormComponent } from '../modal/mission-place-modal-ajout-form/mission-place-modal-ajout-form.component';
import { Specialite } from 'src/app/shared/models/specialite';
import { ErrorResponse } from 'src/app/shared/models/api-response/error-response';
import { saveAs } from 'file-saver';
import { ListingResponse } from 'src/app/shared/models/listing/listing-response';
import { Subject, debounceTime } from 'rxjs';

export type SortOrder = 'asc' | 'desc';

@Component({
	selector: 'app-mission-place',
	templateUrl: './list-mission-place.component.html',
	styleUrls: ['./list-mission-place.component.scss']
})
export class ListMissionPlaceComponent implements OnInit {
	searchedSpeciality = '';
	missionPlaceCsvFile;
	missionPlaces: MissionPlace[] = [];
	currentPage = 1;

	user: User = null;

	isAddable = false;
	isImportable = false;
	isExportable = false;

	sortType = 'name';
	sortOrder: SortOrder = 'asc';
	searchedInput: string = '';

	listSpecialite: Specialite[];
	listSelectedSpecialite: Specialite[] = [];
	specialtiesToFilterBy: string = '';

	ajoutMissionPlaceModalref: BsModalRef;

	// nombre de page totale pour la pagination
	totalItems: number = 0;
	itemsPerPage = 10;

	isMobile = false;
	screenWidth: number;

	isListingLoading: boolean = true;
	firstTimeLoading: boolean = true;

	private searchSubject = new Subject<string>();

	constructor(
		private facade: FacadeService,
		private router: Router,
		private logger: LogService,
		private modalService: BsModalService
	) {
		// ajout de délai à la saisie de recherche pour ne pas bombarder le back de requête
		this.searchSubject
			.pipe(
				debounceTime(300) // Délai en millisecondes
			)
			.subscribe(() => {
				this.searchByInput();
			});
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize() {
		this.screenWidth = window.innerWidth;
		if (this.screenWidth < 1200) {
			this.isMobile = true;
		} else {
			this.isMobile = false;
		}
	}

	onActivate(event) {
		window.scroll(0, 0);
	}

	ngOnInit(): void {
		this.screenWidth = window.innerWidth;
		this.isMobile =
			this.facade.mobileService.isMobile() || this.screenWidth < 1200;

		this.user = this.facade.dataStorageService.getUser();
		if (this.facade.dataStorageService.isPlanificatrice()) {
			this.isImportable = true;
			this.isExportable = true;
		}
		if (
			this.facade.dataStorageService.isProjectManager() ||
			this.facade.dataStorageService.isPlanificatrice()
		) {
			this.isAddable = true;
		}
		this.getMissionPlace();
	}

	getMissionPlace(): void {
		this.isListingLoading = true;

		this.facade.missionPlaceService
			.getMissionPlacesPage(
				this.currentPage - 1, // -1 car la pagination commence à 0 côté back, à 1 côté front
				this.itemsPerPage,
				this.searchedInput.toLowerCase(),
				this.specialtiesToFilterBy
			)
			.subscribe({
				next: (missionPlacesPage: ListingResponse) => {
					this.missionPlaces = missionPlacesPage.dataList;
					this.totalItems = missionPlacesPage.totalItems;

					// si on charge la page pour la première fois, on map les spécialités
					// si on relance getMissionPlace après chargement : ne pas toucher au spécialités, sinon on perd des éléments dans le filtre
					if (this.firstTimeLoading) {
						this.firstTimeLoading = false;
						this.listSpecialite = this.getMissionPlaceSpecialities(
							this.missionPlaces
						);
					}
				},
				error: (err: ErrorResponse) => {
					this.facade.toastService.createHttpErrorToast(
						err.error.response ? err.error.response : '',
						err.status
					);
					this.logger.error(
						'' + err.status + ' ' + err.error.response ? err.error.response : ''
					);
				},
				complete: () => {
					this.isListingLoading = false;
				}
			});
	}

	selectMissionPlace(missionPlace: MissionPlace): void {
		const link = ['/mission-places', missionPlace.id];
		this.router.navigate(link);
	}

	getMissionPlaceSpecialities(missionPlaces: MissionPlace[]): Specialite[] {
		return missionPlaces
			.flatMap((missionPlace) => missionPlace.specialties)
			.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	importMissionPlaces(event: any): void {
		this.isImportable = false;
		this.logger.log('Import de missionPlace');
		this.missionPlaceCsvFile = event.target.files[0];
		this.facade.missionPlaceService
			.importMissionPlaces(this.missionPlaceCsvFile)
			.subscribe({
				complete: () => {
					this.logger.log("C'est importé !");
					this.getMissionPlace();
					this.isImportable = true;
				},
				error: (err: ErrorResponse) => {
					this.isImportable = true;
					this.logger.log("C'est PAS importé !", err);
					this.facade.toastService.createHttpErrorToast(
						err.error.response ? err.error.response : '',
						err.status
					);
				}
			});
	}

	exportMissionPlaces(): void {
		this.facade.exportService
			.exportMissionPlaces()
			.subscribe((blob) => saveAs(blob, 'missionPlaces.csv'));
	}

	openModalAjout() {
		this.ajoutMissionPlaceModalref = this.modalService.show(
			MissionPlaceModalAjoutFormComponent,
			{
				class: 'modal-lg',
				backdrop: 'static'
			}
		);
		this.ajoutMissionPlaceModalref.onHide.subscribe((reason: string | any) => {
			this.getMissionPlace();
		});
	}

	/**
	 * Fonction pour les paramètres de trie. Si le string choisi vaut le string courant l'on change l'ordre de trie (croissant/décroissant)
	 * sinon l'on chance la colonne de trie et l'on trie par ordre crosisant
	 * @param str colonne de trie
	 */
	sortChange(str: string): void {
		if (str === this.sortType) {
			if (this.sortOrder === 'asc') {
				this.sortOrder = 'desc';
			} else {
				this.sortOrder = 'asc';
			}
		} else {
			this.sortType = str;
			this.sortOrder = 'asc';
		}
	}

	/**
	 * Ajoute ou supprime la spécialité à la liste des spécialités sélectionnées
	 * @param e Element html
	 * @param spe String representant la specialite choisie
	 */
	updateSelectedSpecialite(e: any, spe: Specialite) {
		if (e.target.checked) {
			if (!this.listSelectedSpecialite.some((e) => e.id === spe.id)) {
				this.listSelectedSpecialite = [...this.listSelectedSpecialite];
				this.listSelectedSpecialite.push(spe);
			}
		} else {
			if (this.listSelectedSpecialite.some((e) => e.id === spe.id)) {
				this.listSelectedSpecialite = this.listSelectedSpecialite.filter(
					(e) => e.id !== spe.id
				);
			}
		}
	}

	searchByInput() {
		this.currentPage = 1;
		this.getMissionPlace();
	}

	onKeyUp() {
		this.searchSubject.next(this.searchedInput);
	}

	filterListBySpecialties() {
		this.specialtiesToFilterBy = this.listSelectedSpecialite
			.map((s) => s.name)
			.join(',');

		this.currentPage = 1;
		this.getMissionPlace();
	}

	pageHasChanged(pageNumber: any) {
		this.currentPage = pageNumber;
		this.getMissionPlace();
	}
}
